/* @flow */

import classNames from 'classnames';
import * as React from 'react';
import createUseThemeStyles from 'src/shared/app/base/util/createUseThemeStyles';

import styles from './MarketBasePromptCard.style';

import MarketFilterPromptCard from './MarketFilterPromptCard';
import MarketRedirectPromptCard from './MarketRedirectPromptCard';
import MarketFirstInvitationPromptCard from './MarketFirstInvitationPromptCard';
import MarketSearchPromptCard from './MarketSearchPromptCard';
import MarketPendingInvitationsPromptCard from './MarketPendingInvitationsPromptCard';
import {
  guidanceCreateTopic,
  guidanceQuickStart,
  guidanceFirstInvitation,
  guidancePendingInvitations,
  guidanceSearchKeyword,
  guidanceSearchInterest,
} from 'src/shared/app/market/guidance/settings/guidanceSettings';
import MarketCreateTopicCard from './MarketCreateTopicCard';

import useRoutes from 'src/shared/app/base/route/hook/useRoutes';
import { newTopicRoute } from 'src/shared/app/base/route/setting/routeSettings';
import { trackGAEvent } from 'src/shared/core/service/tracking/gaService';

type Props = {
  handleDismiss: () => void,
  // Self passed
  guidanceLabel: string,
};

const useStyles = createUseThemeStyles(styles);

const MarketBasePromptCardDumb = ({
  guidanceLabel,
  handleDismiss,
}: Props): React.Node => {
  const cardProps = { guidanceLabel };

  let cardComponent;
  let cardContent;

  /*
   |----------------------------------------------------------------------------
   | Hooks
   |----------------------------------------------------------------------------
   */

  const [newTopicPath] = useRoutes(newTopicRoute);

  /*
   |----------------------------------------------------------------------------
   | State
   |----------------------------------------------------------------------------
   */

  const [isVisible, setIsVisible] = React.useState(false);

  /*
   |----------------------------------------------------------------------------
   | Effects
   |----------------------------------------------------------------------------
   */

  React.useEffect(() => {
    window.requestAnimationFrame(() => setIsVisible(true));

    trackGAEvent('Market', 'Show Prompt', guidanceLabel);
  }, []);

  /*
   |----------------------------------------------------------------------------
   | Event Handlers
   |----------------------------------------------------------------------------
   */

  const onDismiss = () =>
    window.requestAnimationFrame(() => setIsVisible(false));

  const onTransitionEnd = ({ target }: TransitionEvent) => {
    if (!isVisible) {
      const { current: rootElt } = rootRef;

      if (target === rootElt) {
        handleDismiss();
      }
    }
  };

  /*
   |----------------------------------------------------------------------------
   | Classes
   |----------------------------------------------------------------------------
   */

  const classes = useStyles();

  const rootStyles = classNames(classes.root, {
    [classes['root-isVisible']]: isVisible,
  });

  /*
   |----------------------------------------------------------------------------
   | Refs
   |----------------------------------------------------------------------------
   */

  const rootRef = React.createRef();

  /*
   |----------------------------------------------------------------------------
   | Elements
   |----------------------------------------------------------------------------
   */

  switch (guidanceLabel) {
    case guidanceCreateTopic:
      cardComponent = MarketRedirectPromptCard;
      cardContent = MarketCreateTopicCard;
      // $FlowIssue
      cardProps.route = newTopicPath;
      break;

    case guidanceFirstInvitation:
      cardComponent = MarketFirstInvitationPromptCard;
      break;

    case guidancePendingInvitations:
      cardComponent = MarketPendingInvitationsPromptCard;
      break;

    case guidanceQuickStart:
      cardComponent = MarketFilterPromptCard;
      break;

    case guidanceSearchKeyword:
      cardComponent = MarketSearchPromptCard;
      break;

    case guidanceSearchInterest:
      cardComponent = MarketSearchPromptCard;
      break;
  }

  const cardElt =
    cardComponent &&
    React.createElement(cardComponent, {
      ...(cardContent
        ? {
            children: React.createElement(MarketCreateTopicCard),
          }
        : {}),
      ...cardProps,
      handleDismiss: onDismiss,
    });

  return (
    <div className={rootStyles} ref={rootRef} onTransitionEnd={onTransitionEnd}>
      {cardElt}
    </div>
  );
};

export default React.memo<Props>(MarketBasePromptCardDumb);
